import React from 'react';
import { ClipRenderState } from './renderState';
import RenderProgressBar from './RenderProgressBar';

interface ClipRenderActionsProps {
  renderState: ClipRenderState;
  isRendering: boolean;
  pendingSaves: number;
  renderButtonLabel: string;
  onRender: () => void;
  /** Set when the clip's style can't be rendered — disables + explains why. */
  renderDisabledReason?: string;
  /** Cancels a running generated-style render (shown only while it is cancellable). */
  onCancel?: () => void;
  isCancelling?: boolean;
}

/**
 * Primary render CTA plus its live progress/error feedback. Telegram
 * distribution lives under the Publish section (ClipTelegramActions).
 */
const ClipRenderActions: React.FC<ClipRenderActionsProps> = ({
  renderState,
  isRendering,
  pendingSaves,
  renderButtonLabel,
  onRender,
  renderDisabledReason,
  onCancel,
  isCancelling,
}) => {
  const disabled = isRendering || pendingSaves > 0 || Boolean(renderDisabledReason);
  return (
    <div className="flex flex-col gap-2">
      <RenderProgressBar state={renderState} />
      {onCancel && renderState.status === 'rendering' && renderState.cancellable && (
        <button
          onClick={onCancel}
          disabled={isCancelling}
          className="self-end px-2 py-1 border border-white/20 hover:border-white/50 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-300 disabled:opacity-50"
        >
          {isCancelling ? 'Cancelling…' : 'Cancel render'}
        </button>
      )}
      <button
        onClick={onRender}
        disabled={disabled}
        title={renderDisabledReason || undefined}
        className={`w-full py-3 bg-black border border-white/20 hover:border-white/50
          text-white font-mono text-sm uppercase tracking-[0.15em]
          transition-all active:scale-[0.99]
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isRendering ? 'animate-pulse' : ''}`}
      >
        {renderButtonLabel}
      </button>
      {renderDisabledReason && (
        <p className="text-[10px] font-mono text-zinc-500">{renderDisabledReason}</p>
      )}
    </div>
  );
};

export default ClipRenderActions;
